import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import {
  Scales, MagnifyingGlass, Buildings, WarningCircle, Flag, Plus, CheckCircle,
  Clock, X, ShieldWarning, SpinnerGap, Users, Briefcase, ListChecks, ArrowRight, Tag
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const CATEGORY_COLORS = {
  'Court Calendar': 'bg-blue-50 text-navy border-blue-200',
  'Public Holiday': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Regulatory': 'bg-amber-50 text-amber-700 border-amber-200',
  'Tax': 'bg-red-50 text-red-700 border-red-200',
};

const ISSUE_OPTIONS = [
  'Date has changed', 'Requirement has changed',
  'This no longer applies to the category shown',
  'Regulatory body has changed', 'Other',
];

function LegalCalendar({ user }) {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({ categories: [], regulatory_bodies: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [bodyFilter, setBodyFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [flagModal, setFlagModal] = useState(null);
  const [addModal, setAddModal] = useState(null); // calendar entry being added
  const [placements, setPlacements] = useState({}); // {entry_id: [{placement}]}

  const fetchEntries = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (categoryFilter) params.category = categoryFilter;
      if (bodyFilter) params.regulatory_body = bodyFilter;
      const res = await axios.get(`${API}/legal-calendar`, { params, withCredentials: true });
      setEntries(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, categoryFilter, bodyFilter]);

  const fetchPlacements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/obligations/placements`, { withCredentials: true });
      setPlacements(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchPlacements(); }, [fetchPlacements]);
  useEffect(() => {
    axios.get(`${API}/legal-calendar/filters`, { withCredentials: true })
      .then(res => setFilters(res.data)).catch(() => {});
  }, []);

  const handlePlaced = (entryId, result) => {
    setPlacements(prev => ({
      ...prev,
      [entryId]: [...(prev[entryId] || []), { destination_name: result.matter_name || result.task_title || 'New task', ...result }]
    }));
    setAddModal(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-5xl mx-auto p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-slate-900 mb-1 flex items-center gap-3">
            <Scales size={28} weight="duotone" className="text-navy" />
            Nigerian Legal Reference Calendar
          </h1>
          <p className="text-sm text-slate-500">Court deadlines, regulatory filings, and compliance dates for Nigerian legal professionals</p>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4 mb-6 flex items-start gap-3" data-testid="calendar-disclaimer">
          <ShieldWarning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            This calendar is provided for reference purposes only. Dates, requirements, and applicability should be verified against current primary sources before reliance. Helm Core does not guarantee the completeness or accuracy of this calendar. Regulatory deadlines in Nigeria are subject to change without notice.
          </p>
        </div>

        {/* Search + Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search obligations, deadlines, courts..."
                className="bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
                data-testid="calendar-search" />
            </div>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700" data-testid="calendar-filter-category">
              <option value="">All Categories</option>
              {filters.categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={bodyFilter} onChange={(e) => setBodyFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700" data-testid="calendar-filter-body">
              <option value="">All Regulatory Bodies</option>
              {filters.regulatory_bodies.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <p className="text-xs text-slate-400">{entries.length} entries</p>
        </div>

        {/* Entries */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" /></div>
        ) : entries.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="calendar-empty">
            <Scales size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No entries match your search.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const entryPlacements = placements[entry.entry_id] || [];
              return (
                <motion.div key={entry.entry_id} layout
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-sm transition-all duration-200"
                  data-testid={`calendar-entry-${entry.entry_id}`}>
                  {/* Card content — never affected by placement state */}
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === entry.entry_id ? null : entry.entry_id)}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-sm text-slate-900">{entry.title}</h3>
                          {entry.flagged_for_review && (
                            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                              <Flag size={10} weight="fill" /> Under review
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium border ${CATEGORY_COLORS[entry.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                            {entry.category}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1"><Buildings size={12} /> {entry.regulatory_body}</span>
                          {entry.recurrence_description && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> {entry.recurrence_description}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                          <span className="flex items-center gap-1"><Users size={11} /> <span className="font-medium">Applies to:</span> {entry.who_it_applies_to || 'Verify applicability'}</span>
                          <span>Last verified: {entry.last_verified_date || 'Not verified'}</span>
                        </div>
                      </div>
                      {/* CTA button — only when NOT yet placed */}
                      {entryPlacements.length === 0 && (
                        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => setAddModal(entry)}
                            className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 text-xs"
                            data-testid={`add-obligation-${entry.entry_id}`}>
                            <Plus size={12} weight="bold" /> Add to My Obligations
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Confirmed state — full-width footer bar, separate from card content */}
                  {entryPlacements.length > 0 && (
                    <ConfirmedBar entryId={entry.entry_id} placements={entryPlacements} onAddAnother={() => setAddModal(entry)} />
                  )}

                  <AnimatePresence>
                    {expandedId === entry.entry_id && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-100 px-4 pb-4 pt-3 bg-slate-50/50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-3">
                          {entry.description && <div><p className="font-medium text-slate-500 mb-0.5">Description</p><p className="text-slate-700">{entry.description}</p></div>}
                          {entry.penalty_if_missed && <div><p className="font-medium text-slate-500 mb-0.5">Penalty if Missed</p><p className="text-red-600">{entry.penalty_if_missed}</p></div>}
                          {entry.legislation_ref && <div><p className="font-medium text-slate-500 mb-0.5">Legislation Reference</p><p className="text-slate-700">{entry.legislation_ref}</p></div>}
                          {entry.trigger_event && <div><p className="font-medium text-slate-500 mb-0.5">Trigger Event</p><p className="text-slate-700">{entry.trigger_event} ({entry.day_count} {entry.day_type})</p></div>}
                          {entry.notes && <div className="md:col-span-2"><p className="font-medium text-slate-500 mb-0.5">Notes</p><p className="text-slate-700">{entry.notes}</p></div>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setFlagModal(entry); }}
                          className="text-xs text-slate-400 hover:text-amber-600 transition-colors flex items-center gap-1"
                          data-testid={`flag-entry-${entry.entry_id}`}>
                          <Flag size={12} /> Flag as outdated / Suggest correction
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {flagModal && <FlagModal entry={flagModal} onClose={() => setFlagModal(null)} onSubmitted={() => { setFlagModal(null); fetchEntries(); }} />}
        {addModal && <AddObligationModal entry={addModal} onClose={() => setAddModal(null)} onPlaced={(result) => handlePlaced(addModal.entry_id, result)} />}
      </main>
    </div>
  );
}

/* Confirmed state — full-width footer bar */
function ConfirmedBar({ entryId, placements, onAddAnother }) {
  const [expanded, setExpanded] = useState(false);
  const count = placements.length;
  const label = count === 1
    ? placements[0].destination_name
    : `${count} places`;

  return (
    <div className="border-t border-emerald-100 bg-emerald-50/40 px-4 py-2.5" data-testid={`placed-${entryId}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle size={14} weight="fill" className="text-emerald-500" />
          <span className="text-[11px] text-emerald-700">
            Added to {label}
          </span>
          {count > 1 && (
            <button onClick={() => setExpanded(!expanded)}
              className="text-[11px] text-emerald-600/70 hover:text-emerald-700 underline underline-offset-2">
              {expanded ? 'Hide' : 'View'}
            </button>
          )}
        </div>
        <button onClick={onAddAnother}
          className="text-[11px] text-slate-400 hover:text-navy transition-colors"
          data-testid={`add-again-${entryId}`}>
          + Add to another
        </button>
      </div>
      {expanded && count > 1 && (
        <div className="mt-1.5 pl-6 space-y-0.5">
          {placements.map((p, i) => (
            <p key={i} className="text-[10px] text-emerald-600/80">{p.destination_name}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/* "Add to..." modal with 3 options */
function AddObligationModal({ entry, onClose, onPlaced }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // choose, matter, task, confirm
  const [matters, setMatters] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [matterSearch, setMatterSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    if (step === 'matter') {
      axios.get(`${API}/matters`, { withCredentials: true }).then(r => setMatters(r.data)).catch(() => {});
    }
    if (step === 'task') {
      axios.get(`${API}/tasks?status=pending`, { withCredentials: true }).then(r => setTasks(r.data.items || r.data)).catch(() => {});
    }
  }, [step]);

  const submit = async (mode, targetId = null) => {
    setSaving(true);
    try {
      const payload = { calendar_entry_id: entry.entry_id, mode };
      if (mode === 'matter') payload.matter_id = targetId;
      if (mode === 'existing_task') payload.task_id = targetId;
      const res = await axios.post(`${API}/obligations`, payload, { withCredentials: true });
      setConfirmResult(res.data);
      onPlaced(res.data);
      setStep('confirm');
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const filteredMatters = matterSearch.trim() ? matters.filter(m => m.name.toLowerCase().includes(matterSearch.toLowerCase())) : matters;
  const filteredTasks = taskSearch.trim() ? tasks.filter(t => t.title.toLowerCase().includes(taskSearch.toLowerCase())) : tasks;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-lg md:rounded-lg shadow-lg w-full md:max-w-md max-h-[85vh] flex flex-col"
        data-testid="add-obligation-modal">
        {/* Header */}
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-heading font-medium text-slate-900 truncate">
              {step === 'confirm' ? 'Done' : step === 'choose' ? 'Add to My Obligations' : step === 'matter' ? 'Select a Matter' : 'Select a Task'}
            </h2>
            <p className="text-xs text-slate-400 truncate mt-0.5">{entry.title}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0 ml-3"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Step: Choose */}
          {step === 'choose' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-3">Where would you like to add this?</p>
              <button onClick={() => setStep('matter')} className="w-full text-left p-3.5 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-navy/30 transition-colors flex items-center gap-3" data-testid="choose-matter">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0"><Briefcase size={18} weight="duotone" className="text-navy" /></div>
                <div><p className="text-sm font-medium text-slate-900">Add to a Matter</p><p className="text-xs text-slate-400">Link to a client or project</p></div>
              </button>
              <button onClick={() => setStep('task')} className="w-full text-left p-3.5 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-navy/30 transition-colors flex items-center gap-3" data-testid="choose-task">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0"><ListChecks size={18} weight="duotone" className="text-navy" /></div>
                <div><p className="text-sm font-medium text-slate-900">Add to an existing Task</p><p className="text-xs text-slate-400">Append as a deadline or sub-item</p></div>
              </button>
              <button onClick={() => submit('new_task')} disabled={saving} className="w-full text-left p-3.5 border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-navy/30 transition-colors flex items-center gap-3" data-testid="choose-new-task">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0"><Plus size={18} weight="bold" className="text-navy" /></div>
                <div><p className="text-sm font-medium text-slate-900">{saving ? 'Creating...' : 'Create as a new Task'}</p><p className="text-xs text-slate-400">Standalone task in your list</p></div>
              </button>
              {/* Pre-filled info */}
              <div className="mt-4 pt-3 border-t border-slate-100 space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Pre-filled from calendar</p>
                <p className="text-xs text-slate-600">{entry.title}</p>
                {entry.recurrence_description && <p className="text-xs text-slate-400">{entry.recurrence_description}</p>}
                {entry.penalty_if_missed && <p className="text-xs text-red-600">Penalty: {entry.penalty_if_missed}</p>}
                <div className="flex items-center gap-1 mt-1">
                  <Tag size={10} className="text-slate-400" />
                  <span className="text-[10px] text-slate-400">Nigerian Legal Reference Calendar (read-only tag)</span>
                </div>
              </div>
            </div>
          )}

          {/* Step: Matter */}
          {step === 'matter' && (
            <div>
              <button onClick={() => setStep('choose')} className="text-xs text-slate-400 hover:text-navy mb-3 flex items-center gap-1"><ArrowRight size={10} className="rotate-180" /> Back</button>
              <input type="text" value={matterSearch} onChange={(e) => setMatterSearch(e.target.value)} placeholder="Search matters..." autoFocus
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm mb-3 focus:border-navy focus:ring-2 focus:ring-blue-100" data-testid="matter-search-input" />
              {filteredMatters.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No active matters found.</p> : (
                <div className="space-y-1">
                  {filteredMatters.map(m => (
                    <button key={m.matter_id} onClick={() => submit('matter', m.matter_id)} disabled={saving}
                      className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-3 disabled:opacity-50"
                      data-testid={`select-matter-${m.matter_id}`}>
                      <div className="w-2 h-8 rounded-sm flex-shrink-0" style={{ backgroundColor: m.color || '#1E3A8A' }} />
                      <div className="min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>{m.client && <p className="text-xs text-slate-400">{m.client}</p>}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step: Task */}
          {step === 'task' && (
            <div>
              <button onClick={() => setStep('choose')} className="text-xs text-slate-400 hover:text-navy mb-3 flex items-center gap-1"><ArrowRight size={10} className="rotate-180" /> Back</button>
              <input type="text" value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} placeholder="Search tasks..." autoFocus
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm mb-3 focus:border-navy focus:ring-2 focus:ring-blue-100" data-testid="task-search-input" />
              {filteredTasks.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No pending tasks found.</p> : (
                <div className="space-y-1">
                  {filteredTasks.map(t => (
                    <button key={t.task_id} onClick={() => submit('existing_task', t.task_id)} disabled={saving}
                      className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-3 disabled:opacity-50"
                      data-testid={`select-task-${t.task_id}`}>
                      <ListChecks size={16} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>{t.project_client && <p className="text-xs text-slate-400">{t.project_client}</p>}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step: Confirm */}
          {step === 'confirm' && confirmResult && (
            <div className="text-center py-4">
              <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900 mb-1">
                {confirmResult.type === 'task_in_matter' ? `Added to ${confirmResult.matter_name}` :
                 confirmResult.type === 'subtask_added' ? `Added to ${confirmResult.task_title}` :
                 'New task created'}
              </p>
              <p className="text-xs text-slate-400 mb-4">{entry.title}</p>
              <button onClick={() => {
                if (confirmResult.type === 'task_in_matter') navigate(`/matters/${confirmResult.matter_id}`);
                else navigate('/tasks');
                onClose();
              }}
                className="text-sm text-navy font-medium hover:underline flex items-center gap-1 mx-auto"
                data-testid="view-result-link">
                {confirmResult.type === 'task_in_matter' ? 'View in Matter' : 'View Task'} <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* Flag Modal (unchanged) */
function FlagModal({ entry, onClose, onSubmitted }) {
  const [issueTypes, setIssueTypes] = useState([]);
  const [correction, setCorrection] = useState('');
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleIssue = (issue) => setIssueTypes(prev => prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]);

  const handleSubmit = async () => {
    if (issueTypes.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/legal-calendar/${entry.entry_id}/flag`, { issue_types: issueTypes, suggested_correction: correction || undefined, source_reference: source || undefined }, { withCredentials: true });
      setSubmitted(true);
      setTimeout(() => onSubmitted(), 2500);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="flag-modal">
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <div><h2 className="text-base font-heading font-medium text-slate-900">Flag as Outdated / Suggest Correction</h2><p className="text-xs text-slate-400 mt-0.5">{entry.title}</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        {submitted ? (
          <div className="p-8 text-center">
            <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto mb-3" />
            <p className="text-sm text-slate-700 font-medium mb-1">Thank you</p>
            <p className="text-xs text-slate-500">Your correction has been submitted for review by the Helm Core team. No changes will be made until verified.</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">What is wrong? *</label>
              <div className="space-y-2">
                {ISSUE_OPTIONS.map(issue => (
                  <label key={issue} className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={issueTypes.includes(issue)} onChange={() => toggleIssue(issue)} className="w-4 h-4 rounded border-slate-300 text-navy focus:ring-navy" />
                    <span className="text-sm text-slate-700">{issue}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Your suggested correction <span className="text-slate-400 font-normal">(optional — will be reviewed by the Helm Core team before any update is made)</span></label>
              <textarea value={correction} onChange={(e) => setCorrection(e.target.value.slice(0, 500))} rows={3}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm resize-none focus:border-navy focus:ring-2 focus:ring-blue-100" placeholder="Describe the correct date, updated requirement, or corrected information..." data-testid="flag-correction" />
              <p className="text-[11px] text-slate-400 mt-1">{correction.length}/500</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source or reference <span className="text-slate-400 font-normal">(optional but helpful)</span></label>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm focus:border-navy focus:ring-2 focus:ring-blue-100" placeholder="e.g., FIRS circular link, gazette reference..." data-testid="flag-source" />
            </div>
            <button onClick={handleSubmit} disabled={issueTypes.length === 0 || submitting}
              className="w-full bg-navy text-white hover:bg-navy-hover disabled:opacity-50 font-medium px-4 py-3 rounded-lg text-sm flex items-center justify-center gap-2 transition-all duration-200" data-testid="flag-submit">
              {submitting ? <><SpinnerGap size={14} className="animate-spin" /> Submitting...</> : 'Confirm and Submit'}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default LegalCalendar;
